import { zodResolver } from '@hookform/resolvers/zod';
import { Combobox } from '@optools/components/combobox/combobox';
import { MultiSelect } from '@optools/components/multi-select';
import { Button } from '@optools/components/ui/button';
import { DialogClose, DialogFooter } from '@optools/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@optools/components/ui/form';
import { Input } from '@optools/components/ui/input';
import { SessionOverride } from '@optools/services/operator-gateway/overrides/session/types';
import { ClientType } from '@optools/services/operator-gateway/overrides/types';
import { SessionFeature } from '@optools/services/operator-gateway/types/generated/graphql';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const schema = z.object({
  clientType: z.enum(ClientType),
  features: z.enum(SessionFeature).array(),
  userId: z.string().min(1, { error: 'Okta ID is required' }),
});

export type SessionOverrideFormValues = z.infer<typeof schema>;

export interface SessionOverrideFormProps {
  loading: boolean;
  onSubmit: (values: SessionOverrideFormValues) => void;
  override?: SessionOverride | null;
}

const { ALL_CLIENTS, INTERNAL, ...restOfClientTypes } = ClientType;

const CLIENT_TYPE_OPTIONS = [ALL_CLIENTS, INTERNAL, ...Object.keys(restOfClientTypes)].map(
  (value) => ({
    label: value,
    value,
  }),
);

const FEATURE_OPTIONS = Object.keys(SessionFeature).map((value) => ({
  label: value,
  value,
}));

export function SessionOverrideForm({ loading, onSubmit, override }: SessionOverrideFormProps) {
  const { clientType = ALL_CLIENTS, features = [], userId = '' } = override || {};

  const form = useForm<SessionOverrideFormValues>({
    defaultValues: { clientType, features, userId },
    disabled: loading,
    resolver: zodResolver(schema),
  });

  return (
    <Form {...form}>
      <form aria-label="override" className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="userId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Okta ID</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="clientType"
          render={({ field }) => {
            const { onChange, ...rest } = field;
            return (
              <FormItem>
                <FormLabel>Client Type</FormLabel>
                <FormControl>
                  <Combobox data={CLIENT_TYPE_OPTIONS} onValueChange={onChange} {...rest} />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="features"
          render={({ field }) => {
            return (
              <FormItem>
                <FormLabel>Features</FormLabel>
                <FormControl>
                  <MultiSelect
                    disabled={field.disabled}
                    options={FEATURE_OPTIONS}
                    placeholder="Select features..."
                    selected={field.value}
                    onChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            );
          }}
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button disabled={loading} variant="secondary">
              Cancel
            </Button>
          </DialogClose>
          <Button disabled={loading} loading={loading} type="submit">
            Save
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
